import { type Basket, type Location } from '@koala/sdk';
import { addDays } from 'date-fns';
import { utcToZonedTime, zonedTimeToUtc } from 'date-fns-tz';
import { GIFT_CARD_WINDOW_TIME_ZONE } from '@/constants/giftCard';
import { isAsapValue } from '@/utils/conveyance';

/**
 * True when a scheduled `wantedAt` falls beyond the location's Valutec gift-card window
 * (`gift_card_max_days_ahead`). (TEAM-15105)
 *
 * Mirrors chowly-api `ValutecGiftCardPayment.within_scheduling_window?` (`parsed <= N.days.from_now`)
 * and coo-api's check: no window (`null` or absent), ASAP and an unparseable time are all allowed,
 * and otherwise the slot is allowed up to and including the window's last instant. `0` is a real
 * limit.
 *
 * That last instant is N calendar days from now in Central time, the same wall-clock time N days
 * later, not N × 24 hours. Across a DST change the two differ by an hour: 119 hours ahead when the
 * clocks spring forward, 121 when they fall back.
 *
 * What is left: date-fns-tz v1 moves the wall-clock time through the browser's own zone, so the
 * result can be an hour off the server's when that Central time falls in a skipped or repeated hour.
 * That can be Central's own (1–3 AM on its transition days) or the browser's: a London browser
 * skips 01:00–02:00 on Europe's spring-forward day. For a US browser the two coincide, so it is
 * only Central's. The server stays authoritative either way. A slot this lets through can still be
 * refused on apply, and that refusal shows coo-api's `user_message`.
 */
export const isOutsideGiftCardWindow = (
  maxDaysAhead: number | null | undefined,
  wantedAt: string | null | undefined,
  now: Date = new Date(),
): boolean => {
  if (typeof maxDaysAhead !== 'number' || !wantedAt || isAsapValue(wantedAt)) {
    return false;
  }

  const wantedMs = new Date(wantedAt).getTime();
  if (!Number.isFinite(wantedMs)) {
    return false;
  }

  const windowEnd = zonedTimeToUtc(
    addDays(utcToZonedTime(now, GIFT_CARD_WINDOW_TIME_ZONE), maxDaysAhead),
    GIFT_CARD_WINDOW_TIME_ZONE,
  );

  return wantedMs > windowEnd.getTime();
};

/**
 * The location's `gift_card_scheduling_message` when `wantedAt` is outside its gift-card window,
 * otherwise `null`. coo-api authors the sentence; the storefront only decides when to show it, and a
 * missing or blank message shows nothing. (TEAM-15105)
 */
export const getGiftCardWindowMessage = (
  location:
    | Pick<Location, 'gift_card_max_days_ahead' | 'gift_card_scheduling_message'>
    | null
    | undefined,
  wantedAt: string | null | undefined,
  now: Date = new Date(),
): string | null => {
  const message = location?.gift_card_scheduling_message;

  return typeof message === 'string' &&
    message.trim() !== '' &&
    isOutsideGiftCardWindow(location?.gift_card_max_days_ahead, wantedAt, now)
    ? message
    : null;
};

/**
 * True when a coo-api basket explicitly reports no gift-card amount authorized (a number `<= 0`).
 * An absent field says nothing either way, so it is `false`. (TEAM-15105)
 */
export const basketReportsNoGiftCard = (
  basket: Pick<Basket, 'gift_card_amount_authorized'>,
): boolean =>
  typeof basket.gift_card_amount_authorized === 'number' && basket.gift_card_amount_authorized <= 0;
